import type { Node } from "@xyflow/react";

export interface WorkflowContractAddress {
  address: string;
  name?: string;
  chainId?: number;
}

/**
 * Extracts all contract addresses used in a workflow
 * Searches through all node payloads for address fields
 */
export function extractWorkflowContractAddresses(nodes: Node[]): string[] {
  const addresses = new Set<string>();

  nodes.forEach(node => {
    if (!node.data?.payload) return;

    const payload = node.data.payload as Record<string, unknown>;

    // Look for contract address fields
    Object.entries(payload).forEach(([key, value]) => {
      if (
        (key.toLowerCase().includes("contractaddress") ||
          key.toLowerCase().includes("contract_address") ||
          key.toLowerCase() === "contract") &&
        typeof value === "string" &&
        value.startsWith("0x") &&
        value.length === 42
      ) {
        addresses.add(value.toLowerCase());
      }
    });
  });

  return Array.from(addresses);
}

/**
 * Extracts contract addresses with metadata (name, chainId) from workflow
 */
export async function extractWorkflowContractAddressesWithNames(nodes: Node[]): Promise<WorkflowContractAddress[]> {
  const addressMap = new Map<string, WorkflowContractAddress>();

  nodes.forEach(node => {
    if (!node.data?.payload) return;

    const payload = node.data.payload as Record<string, unknown>;
    const chainId = payload.chainId as number | undefined;

    // Look for contract address fields
    Object.entries(payload).forEach(([key, value]) => {
      if (
        (key.toLowerCase().includes("contractaddress") ||
          key.toLowerCase().includes("contract_address") ||
          key.toLowerCase() === "contract") &&
        typeof value === "string" &&
        value.startsWith("0x") &&
        value.length === 42
      ) {
        const address = value.toLowerCase();
        if (!addressMap.has(address)) {
          addressMap.set(address, {
            address,
            chainId,
          });
        }
      }
    });
  });

  // Fetch names for all addresses
  const results = await Promise.all(
    Array.from(addressMap.values()).map(async item => {
      if (item.chainId) {
        try {
          const response = await fetch(`https://contract-abi-api.b3.fun/list?chainId=${item.chainId}&limit=100`);
          const data = await response.json();
          const contract = data.results?.find((c: any) => c.address.toLowerCase() === item.address);
          if (contract) {
            return { ...item, name: contract.name || contract.nameTag };
          }
        } catch (error) {
          console.error("Failed to fetch contract name:", error);
        }
      }
      return item;
    }),
  );

  return results;
}
